import { Add, Archive, ContentCopy, Delete, Edit, KeyboardArrowDown, KeyboardArrowRight, MoreVert, UploadFile } from '@mui/icons-material';
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, IconButton, InputLabel, LinearProgress, Link, Menu, MenuItem, Paper, Select, Table, TableBody, TableCell, TableHead, TableRow, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import Pagination from '@mui/material/Pagination';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { navigate } from '@reach/router';
import React, { useEffect, useState } from 'react';
import apiClient from '../api/client';
import AdvancedFilters from '../components/AdvancedFilters';
import AsyncSearchSelect from '../components/AsyncSearchSelect';
import AsyncTableSearch from '../components/AsyncTableSearch';
import ExcelImportDialog from '../components/ExcelImportDialog';
import Layout from '../components/Layout';
import SchemaEditor from '../components/SchemaEditor';
import StoragePickerDialog from '../components/StoragePickerDialog';
import StyledTableContainer from '../components/StyledTableContainer';
import { useAuth } from '../contexts/AuthContext';
import { useRealtimeSSE } from '../hooks/useRealtimeSSE';

const ProductsPage = () => {
  const { user, loading: authLoading } = useAuth();

  // ✅ Подключение к SSE для real-time обновлений
  useRealtimeSSE();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [advancedFilters, setAdvancedFilters] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category_id: '',
    stock: '0'
  });
  const [productSchema, setProductSchema] = useState([]);
  const [schemaDialogOpen, setSchemaDialogOpen] = useState(false);
  const [page, setPage] = useState(1);
  const perPage = 20;
  const [total, setTotal] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  // Состояние для пагинации поиска
  const [searchActive, setSearchActive] = useState(false);
  const [searchPage, setSearchPage] = useState(1);
  const [searchPagination, setSearchPagination] = useState({ page: 1, pages: 1, total: 0, perPage: 20 });
  const [presignedMap, setPresignedMap] = useState({});
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerImagesOnly, setPickerImagesOnly] = useState(false);
  const [pickerTargetField, setPickerTargetField] = useState(null);
  const [density, setDensity] = useState('standard'); // compact | standard | comfortable
  const [collapsedCategories, setCollapsedCategories] = useState({}); // { [categoryName]: boolean }
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [actionMenuProduct, setActionMenuProduct] = useState(null);
  const [productCacheById, setProductCacheById] = useState({});

  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));

  const allFilterFields = [
    { value: 'name', label: 'Название' },
    { value: 'description', label: 'Описание' },
    { value: 'price', label: 'Цена' },
    { value: 'stock', label: 'Остаток' },
    { value: 'category', label: 'Категория' },
    ...productSchema.map(f => ({ value: f.name, label: f.name })),
  ];

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/auth');
      return;
    }
    if (user) {
      fetchCategories();
      fetchProducts();
      fetchSchema();
    }
  }, [user, authLoading]);

  useEffect(() => {
    // Если активен серверный поиск - не делаем локальную фильтрацию
    // (результаты уже отфильтрованы на сервере через AsyncTableSearch)
    if (searchActive) return;

    if (products.length > 0) {
      let filtered = products;

      // Применяем расширенные фильтры (локально)
      if (advancedFilters.length > 0) {
        filtered = filtered.filter(product => {
          return advancedFilters.every(filter => {
            const value = filter.field === 'category'
              ? getCategoryName(product.category_id)
              : product[filter.field];

            if (value === null || value === undefined) return false;

            const stringValue = String(value).toLowerCase();
            const filterValue = filter.value.toLowerCase();

            switch (filter.operator) {
              case 'contains':
                return stringValue.includes(filterValue);
              case 'equals':
                return stringValue === filterValue;
              case 'startsWith':
                return stringValue.startsWith(filterValue);
              case 'endsWith':
                return stringValue.endsWith(filterValue);
              case 'greaterThan':
                return Number(value) > Number(filter.value);
              case 'lessThan':
                return Number(value) < Number(filter.value);
              default:
                return true;
            }
          });
        });
      }

      setFilteredProducts(filtered);
    } else {
      setFilteredProducts([]);
    }
  }, [products, categories, advancedFilters, searchActive]);

  const fetchCategories = async () => {
    try {
      const response = await apiClient.get('/categories');
      setCategories(response.data);
      console.log(response.data)
    } catch (err) {
      console.error('Ошибка при загрузке категорий:', err);
    }
  };

  const fetchProducts = async (pageParam = page) => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/products?page=${pageParam}&per_page=${perPage}`);

      // API client подменяет response.data на массив и сохраняет пагинацию в response.pagination
      const productsData = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      const pagination = response.pagination || response.data || {};

      setProducts(productsData);

      // Используем данные из pagination объекта
      if (pagination.total !== undefined) {
        setTotal(pagination.total);
      }

      const pages = pagination.pages || pagination.total_pages || 1;
      setTotalPages(pages);

      // Проверяем есть ли следующая страница
      setHasNext(pageParam < pages);

      setError('');
    } catch (err) {
      console.error('Ошибка при загрузке продуктов:', err);
      setError('Не удалось загрузить список продуктов');
    } finally {
      setLoading(false);
    }
  };

  const fetchSchema = async () => {
    try {
      const resp = await apiClient.get('/products/schema');
      setProductSchema(resp.data.schema || []);
    } catch (err) {
      console.error('Не удалось получить схему товаров', err);
    }
  };

  // Вспомогательные компоны

  const getStockColor = (stockValue) => {
    if (stockValue < 10) return 'error';
    if (stockValue < 30) return 'warning';
    return 'success';
  };

  const getDiscountPercent = (product) => {
    const byField = Number(product.discount_percent || product.discount || 0);
    if (byField > 0) return Math.round(byField);
    const oldPrice = Number(product.old_price || product.previous_price || 0);
    const price = Number(product.price || 0);
    if (oldPrice > price && price > 0) {
      return Math.round(((oldPrice - price) / oldPrice) * 100);
    }
    return 0;
  };

  const getBadgesForProduct = (product) => {
    const badges = [];
    // Категория
    const catName = getCategoryName(product.category_id);
    if (catName) badges.push({ label: catName, color: 'default', variant: 'outlined', key: 'category' });
    // Новинка
    if (product.is_new || product.new) badges.push({ label: 'Новинка', color: 'primary', key: 'new' });
    // Хит
    if (product.is_hit || product.best_seller || product.bestseller) badges.push({ label: 'Хит', color: 'secondary', key: 'hit' });
    // Скидка
    const discount = getDiscountPercent(product);
    if (discount > 0) badges.push({ label: `-${discount}%`, color: 'success', key: 'discount' });
    // Нет в наличии
    if (Number(product.stock) === 0) badges.push({ label: 'Нет в наличии', color: 'warning', key: 'oos' });
    return badges;
  };



  const handleToggleCategory = (categoryName) => {
    setCollapsedCategories((prev) => ({ ...prev, [categoryName]: !prev[categoryName] }));
  };

  const handleDensityChange = (event, next) => {
    if (next) setDensity(next);
  };

  const rowPy = density === 'compact' ? 0.5 : density === 'comfortable' ? 2 : 1;
  const effectiveRowPy = isXs ? 0.5 : rowPy;
  const cellSxBase = { color: 'white', py: effectiveRowPy };

  const openActionsMenu = (event, product) => {
    setActionMenuAnchor(event.currentTarget);
    setActionMenuProduct(product);
  };
  const closeActionsMenu = () => {
    setActionMenuAnchor(null);
    setActionMenuProduct(null);
  };

  const handleOpenDialog = (product = null) => {
    if (product) {
      setEditingProduct(product);
      const dynamicValues = {};
      productSchema.forEach(f => {
        dynamicValues[f.name] = product[f.name] ?? '';
      });
      setFormData({
        name: product.name,
        description: product.description || '',
        price: product.price.toString(),
        category_id: product.category_id ? product.category_id.toString() : '',
        stock: product.stock.toString(),
        ...dynamicValues
      });
    } else {
      setEditingProduct(null);
      const dynamicDefaults = {};
      productSchema.forEach(f => {
        dynamicDefaults[f.name] = f.default ?? '';
      });
      setFormData({
        name: '',
        description: '',
        price: '',
        category_id: categories.length > 0 ? categories[0].id.toString() : '',
        stock: '0',
        ...dynamicDefaults
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Форматирование данных для API
    const productData = {
      ...formData,
      price: parseFloat(formData.price),
      category_id: formData.category_id || null,
      stock: parseInt(formData.stock)
    };

    try {
      if (editingProduct) {
        await apiClient.put(`/products/${editingProduct.id}`, productData);
      } else {
        await apiClient.post('/products', productData);
      }
      handleCloseDialog();
      fetchProducts();
    } catch (err) {
      console.error('Ошибка при сохранении продукта:', err);
      setError('Не удалось сохранить данные продукта');
    }
  };

  const handleDelete = async (productId) => {
    if (window.confirm('Вы уверены, что хотите удалить этот продукт?')) {
      try {
        await apiClient.delete(`/products/${productId}`);
        fetchProducts();
      } catch (err) {
        console.error('Ошибка при удалении продукта:', err);
        setError('Не удалось удалить продукт');
      }
    }
  };

  const getCategoryName = (categoryId) => {
    if (categoryId == null) return '';
    try {
      const match = categories.find(c => c && c.id != null && String(c.id) === String(categoryId));
      return match && match.name ? match.name : '';
    } catch (e) {
      return '';
    }
  };

  const handleApplyFilters = (filters) => {
    setAdvancedFilters(filters);
  };

  const handleResetFilters = () => {
    setAdvancedFilters([]);
  };

  const openSchemaDialog = () => setSchemaDialogOpen(true);
  const closeSchemaDialog = () => setSchemaDialogOpen(false);

  const handleSaveSchema = async () => {
    try {
      await apiClient.put('/products/schema', { schema: productSchema });
      closeSchemaDialog();
      fetchProducts();
    } catch (err) {
      console.error('Не удалось сохранить схему', err);
    }
  };

  const handlePageChange = (event, value) => {
    if (searchActive) {
      // При активном поиске меняем страницу поиска
      setSearchPage(value);
    } else {
      // Обычная пагинация
      setPage(value);
      fetchProducts(value);
    }
  };

  // Callback для результатов поиска
  const handleSearchResults = (items) => {
    setFilteredProducts(items);
  };

  // Callback для изменения пагинации поиска
  const handleSearchPaginationChange = (paginationInfo) => {
    setSearchPagination(paginationInfo);
  };

  // Callback для изменения активности поиска
  const handleSearchActiveChange = (active) => {
    setSearchActive(active);
    if (!active) {
      // Сбрасываем на основные данные при очистке поиска
      setFilteredProducts(products);
      setSearchPage(1);
      setSearchPagination({ page: 1, pages: 1, total: 0, perPage: 20 });
    }
  };

  const uploadForField = async (fieldName, fileObj) => {
    try {
      const form = new FormData();
      form.append('file', fileObj);
      form.append('prefix', `products/`);
      const resp = await apiClient.post('/storage/files', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const key = resp.data?.key;
      if (key) {
        setFormData(prev => ({ ...prev, [fieldName]: key }));
      }
    } catch (e) {
      console.error('Ошибка загрузки файла', e);
      setError('Ошибка загрузки файла');
    }
  };

  const fetchPresigned = async (key) => {
    if (!key || presignedMap[key]) return presignedMap[key];
    try {
      const resp = await apiClient.get(`/storage/files/${encodeURIComponent(key)}/url`);
      const url = resp.data?.url;
      if (url) {
        setPresignedMap(prev => ({ ...prev, [key]: url }));
        return url;
      }
    } catch (e) {
      console.error('Ошибка получения ссылки на файл', e);
    }
    return null;
  };

  const renderFileField = (field, isImage) => {
    const key = formData[field.name];
    const fileName = key ? String(key).split('/').pop() : '';
    return (
      <Box>
        {key ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {isImage && (
              <Box sx={{ width: 120, height: 80, borderRadius: 1, overflow: 'hidden', bgcolor: 'rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {presignedMap[key] ? (
                  <img src={presignedMap[key]} alt={fileName} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'cover' }} />
                ) : (
                  <Button size="small" variant="outlined" onClick={async () => { await fetchPresigned(key); }}>
                    Показать превью
                  </Button>
                )}
              </Box>
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2">{fileName}</Typography>
              <Button size="small" onClick={async () => {
                const url = await fetchPresigned(key);
                if (url) window.open(url, '_blank');
              }}>Открыть</Button>
            </Box>
            <Button variant="outlined" size="small" component="label">
              Заменить файл
              <input type="file" hidden onChange={(e) => e.target.files && e.target.files[0] && uploadForField(field.name, e.target.files[0])} />
            </Button>
            <Button variant="text" size="small" onClick={() => { setPickerTargetField(field.name); setPickerImagesOnly(!!isImage); setPickerOpen(true); }}>
              Выбрать из хранилища
            </Button>
          </Box>
        ) : (
          <Button variant="outlined" size="small" component="label">
            Загрузить {isImage ? 'изображение' : 'файл'}
            <input type="file" accept={isImage ? 'image/*' : undefined} hidden onChange={(e) => e.target.files && e.target.files[0] && uploadForField(field.name, e.target.files[0])} />
          </Button>
        )}
        {!key && (
          <Button sx={{ ml: 2 }} variant="text" size="small" onClick={() => { setPickerTargetField(field.name); setPickerImagesOnly(!!isImage); setPickerOpen(true); }}>
            Выбрать из хранилища
          </Button>
        )}
      </Box>
    );
  };

  return (
    <Layout>
      <Box sx={{
        p: 3,
        background: 'rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(10px)',
        borderRadius: '16px',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
      }}>
        <Box sx={{ mb: 4, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: { sm: 'space-between' }, gap: { xs: 2, sm: 0 } }}>
          <Typography variant="h4" component="h1" sx={{ mr: { sm: 'auto' } }}>
            Продукты
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, width: { xs: '100%', sm: 'auto' }, gap: { xs: 1, sm: 2 } }}>
            <Button
              variant="contained"
              color="success"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              Добавить продукт
            </Button>
            <Button
              variant="outlined"
              startIcon={<UploadFile />}
              onClick={() => setImportOpen(true)}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              Импорт из Excel
            </Button>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexDirection: { xs: 'column', sm: 'row' } }}>
          <AsyncTableSearch
            value={searchQuery}
            onChange={setSearchQuery}
            onResults={handleSearchResults}
            onActiveChange={handleSearchActiveChange}
            onPaginationChange={handleSearchPaginationChange}
            onLoadingChange={(loading) => {
              if (loading) {
                // Не показываем «нет результатов» во время запроса
                // Здесь можно показать спиннер/статус над таблицей, пока оставляем список как есть
              }
            }}
            placeholder="Поиск продуктов..."
            searchDoc="products"
            searchFields={["name", "description"]}
            page={searchPage}
            perPage={perPage}
            sx={{ flex: 1, width: { xs: '100%', sm: 'auto' } }}
          />
          <AdvancedFilters
            filters={advancedFilters}
            onApply={handleApplyFilters}
            onReset={handleResetFilters}
            fields={allFilterFields}
          />
        </Box>

        {categories.length === 0 && (
          <Paper sx={{ p: 2, mb: 2, bgcolor: 'warning.light' }}>
            Для добавления продуктов сначала создайте категорию в разделе "Категории"
          </Paper>
        )}

        {error && (
          <Paper sx={{ p: 2, mb: 2, bgcolor: 'error.light', color: 'error.contrastText' }}>
            {error}
          </Paper>
        )}
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <ToggleButtonGroup size="small" color="primary" value={density} exclusive onChange={handleDensityChange} sx={{ display: { xs: 'none', sm: 'inline-flex' } }}>
            <ToggleButton value="compact">Компактный</ToggleButton>
            <ToggleButton value="standard">Стандарт</ToggleButton>
            <ToggleButton value="comfortable">Просторный</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <StyledTableContainer component={Paper}>
          <Table stickyHeader size={isXs ? 'small' : 'medium'}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...cellSxBase, position: { xs: 'static', md: 'sticky' }, left: { md: 0 }, background: { md: 'rgba(30,30,30,0.9)' }, zIndex: { md: 3 } }}>Название</TableCell>
                <TableCell sx={{ ...cellSxBase, display: { xs: 'none', sm: 'table-cell' } }}>Бейджи</TableCell>
                <TableCell sx={{ ...cellSxBase, display: { xs: 'none', sm: 'table-cell' } }}>Цена</TableCell>
                <TableCell sx={cellSxBase}>Остаток</TableCell>

                {productSchema.map((f) => (
                  <TableCell key={f.name} sx={{ ...cellSxBase, display: { xs: 'none', md: 'table-cell' } }}>{f.name}</TableCell>
                ))}
                <TableCell sx={{ ...cellSxBase, display: { xs: 'none', md: 'table-cell' } }}>Описание</TableCell>
                <TableCell sx={{ ...cellSxBase, display: { xs: 'none', sm: 'table-cell' }, position: { sm: 'sticky' }, right: { sm: 0 }, background: { sm: 'rgba(30,30,30,0.9)' }, zIndex: { sm: 3 } }}>Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell sx={cellSxBase} colSpan={999} align="center">Загрузка...</TableCell>
                </TableRow>
              ) : filteredProducts.length === 0 ? (
                <TableRow>
                  <TableCell sx={cellSxBase} colSpan={999} align="center">
                    {searchQuery ? 'Продукты не найдены' : 'Продукты не найдены'}
                  </TableCell>
                </TableRow>
              ) : (
                // Группировка по категории
                Object.entries(
                  filteredProducts.reduce((acc, p) => {
                    const catRaw = getCategoryName(p.category_id);
                    const cat = catRaw && typeof catRaw === 'string' && catRaw.trim() ? catRaw : 'Без категории';
                    if (!acc[cat]) acc[cat] = [];
                    acc[cat].push(p);
                    return acc;
                  }, {})
                ).map(([catName, items]) => (
                  <React.Fragment key={catName}>
                    <TableRow sx={{ background: 'rgba(255,255,255,0.05)' }}>
                      <TableCell colSpan={999} sx={{ py: 0.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <IconButton size="small" onClick={() => handleToggleCategory(catName)}>
                            {collapsedCategories[catName] ? <KeyboardArrowRight /> : <KeyboardArrowDown />}
                          </IconButton>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{catName}</Typography>
                          <Typography variant="caption" color="text.secondary">{items.length}</Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                    {!collapsedCategories[catName] && items.map((product) => {
                      const badges = getBadgesForProduct(product);
                      const discount = getDiscountPercent(product);
                      const stockVal = Number(product.stock || 0);
                      const stockPct = Math.min(100, Math.round((stockVal / 30) * 100));
                      return (
                        <TableRow key={product.id}
                          sx={{
                            '&:nth-of-type(odd)': { backgroundColor: 'rgba(255,255,255,0.03)' },
                            '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' }
                          }}
                        >
                          <TableCell sx={{ ...cellSxBase, position: { xs: 'static', md: 'sticky' }, left: { md: 0 }, background: { md: 'rgba(30,30,30,0.85)' }, zIndex: { md: 2 }, minWidth: 220 }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="body1" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>{product.name}</Typography>
                                {/* Mobile inline details */}
                                <Box sx={{ display: { xs: 'flex', sm: 'none' }, alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                                  <Typography variant="body2">{product.price.toLocaleString()} ₸</Typography>
                                  {discount > 0 && (
                                    <Chip size="small" color="success" label={`-${discount}%`} />
                                  )}
                                </Box>
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                                  {badges.map(b => (
                                    <Chip key={b.key} label={b.label} size="small" color={b.color} variant={b.variant || 'filled'} sx={{ display: { xs: 'inline-flex', sm: 'none' } }} />
                                  ))}
                                </Box>
                              </Box>
                              <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                                <IconButton size="small" onClick={(e) => openActionsMenu(e, product)}>
                                  <MoreVert fontSize="small" />
                                </IconButton>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell sx={{ ...cellSxBase, display: { xs: 'none', sm: 'table-cell' } }}>
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              {badges.map(b => (
                                <Chip key={b.key} label={b.label} size="small" color={b.color} variant={b.variant || 'filled'} />
                              ))}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ ...cellSxBase, display: { xs: 'none', sm: 'table-cell' } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography>{product.price.toLocaleString()} ₸</Typography>
                              {discount > 0 && (
                                <Chip size="small" color="success" label={`-${discount}%`} />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell sx={cellSxBase}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: { xs: 80, sm: 140 } }}>
                              <LinearProgress variant="determinate" value={stockPct} color={getStockColor(stockVal)} sx={{ display: { xs: 'none', sm: 'inline-flex' }, width: 90, height: 8, borderRadius: 6 }} />
                              <Box sx={{ display: { xs: 'inline-flex', sm: 'none' }, width: 10, height: 10, borderRadius: '50%', bgcolor: (theme) => theme.palette[getStockColor(stockVal)].main }} />
                              <Typography variant="body2" color="text.secondary">{stockVal}</Typography>
                            </Box>
                          </TableCell>

                          {productSchema.map((field) => (
                            field.type === 'select' ? (
                              <TableCell key={field.name} sx={cellSxBase}>
                                <FormControl fullWidth margin="normal">
                                  <InputLabel id={`label-${field.name}`}>{field.name}</InputLabel>
                                  <Select
                                    labelId={`label-${field.name}`}
                                    name={field.name}
                                    value={product[field.name] ?? ''}
                                    label={field.name}
                                    onChange={(e) => handleChange({ target: { name: field.name, value: e.target.value } })}
                                    MenuProps={{
                                      PaperProps: { style: { width: '100%' } }
                                    }}
                                  >
                                    {(field.options || []).map((opt) => (
                                      <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </TableCell>
                            ) : (
                              <TableCell key={field.name} sx={cellSxBase}>
                                {field.type === 'boolean' ? (
                                  product[field.name] ? 'Да' : 'Нет'
                                ) : field.type === 'image' ? (
                                  product[field.name] ? (
                                    <img
                                      alt={field.name}
                                      src={presignedMap[product[field.name]] || ''}
                                      style={{ maxWidth: 80, maxHeight: 60, objectFit: 'cover' }}
                                      onMouseEnter={async () => { if (!presignedMap[product[field.name]]) await fetchPresigned(product[field.name]); }}
                                    />
                                  ) : '—'
                                ) : field.type === 'file' ? (
                                  product[field.name] ? (
                                    <Link
                                      component="button"
                                      variant="body2"
                                      onClick={async () => {
                                        const url = await fetchPresigned(product[field.name]);
                                        if (url) window.open(url, '_blank');
                                      }}
                                    >
                                      {String(product[field.name]).split('/').pop()}
                                    </Link>
                                  ) : '—'
                                ) : (
                                  product[field.name] ?? '—'
                                )}
                              </TableCell>
                            )
                          ))}
                          <TableCell sx={{ ...cellSxBase, display: { xs: 'none', sm: 'table-cell' } }}>{product.description || '—'}</TableCell>
                          <TableCell sx={{ ...cellSxBase, display: { xs: 'none', sm: 'table-cell' }, position: { sm: 'sticky' }, right: { sm: 0 }, background: { sm: 'rgba(30,30,30,0.75)' }, zIndex: { sm: 2 } }}>
                            <Box sx={{ display: 'flex', gap: 0.5, opacity: 0.0, transition: 'opacity .2s', '.MuiTableRow-root:hover &': { opacity: 1 } }}>
                              <Tooltip title="Редактировать"><IconButton size="small" onClick={async () => {
                                const id = product.id || product._id;
                                if (id && !productCacheById[id]) {
                                  try {
                                    const resp = await apiClient.get(`/products/${id}`);
                                    const full = resp.data || product;
                                    setProductCacheById(prev => ({ ...prev, [id]: full }));
                                    handleOpenDialog(full);
                                    return;
                                  } catch (e) { }
                                }
                                handleOpenDialog(productCacheById[product.id] || product);
                              }}><Edit fontSize="small" /></IconButton></Tooltip>
                              <Tooltip title="Дублировать"><IconButton size="small" onClick={() => handleOpenDialog({ ...product, name: `${product.name} (копия)` })}><ContentCopy fontSize="small" /></IconButton></Tooltip>
                              <Tooltip title="Архивировать"><span><IconButton size="small" disabled><Archive fontSize="small" /></IconButton></span></Tooltip>
                              <Tooltip title="Удалить"><IconButton size="small" onClick={() => handleDelete(product.id)}><Delete fontSize="small" /></IconButton></Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </StyledTableContainer>

        {/* Mobile actions menu */}
        <Menu
          anchorEl={actionMenuAnchor}
          open={Boolean(actionMenuAnchor)}
          onClose={closeActionsMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem onClick={() => { if (actionMenuProduct) handleOpenDialog(actionMenuProduct); closeActionsMenu(); }}>Редактировать</MenuItem>
          <MenuItem onClick={() => { if (actionMenuProduct) handleOpenDialog({ ...actionMenuProduct, name: `${actionMenuProduct.name} (копия)` }); closeActionsMenu(); }}>Дублировать</MenuItem>
          <MenuItem disabled>Архивировать</MenuItem>
          <MenuItem onClick={() => { if (actionMenuProduct) handleDelete(actionMenuProduct.id); closeActionsMenu(); }} sx={{ color: 'error.main' }}>Удалить</MenuItem>
        </Menu>

        {/* Пагинация: разные данные для поиска и обычного режима */}
        {(() => {
          const currentPage = searchActive ? searchPagination.page : page;
          const currentTotalPages = searchActive ? searchPagination.pages : totalPages;
          const currentTotal = searchActive ? searchPagination.total : total;

          if (currentTotalPages <= 1 && !searchActive) return null;

          return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 3, gap: 2, flexWrap: 'wrap' }}>
              {searchActive && (
                <Typography variant="body2" color="text.secondary">
                  Найдено: {currentTotal}
                </Typography>
              )}
              {currentTotalPages > 1 && (
                <Pagination
                  count={currentTotalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                />
              )}
            </Box>
          );
        })()}
      </Box>

      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>{editingProduct ? 'Редактировать продукт' : 'Добавить продукт'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField
              fullWidth
              label="Название"
              name="name"
              value={formData.name}
              onChange={handleChange}
              margin="normal"
              required
            />
            <AsyncSearchSelect
              name="category_id"
              label="Категория"
              value={formData.category_id}
              onChange={handleChange}
              searchDoc="categories"
              searchField="name"
              fallbackOptions={categories}
              getOptionId={(o) => o.id?.toString?.()}
              getOptionLabel={(o) => o.name || ''}
              required
            />
            <TextField
              fullWidth
              label="Цена"
              name="price"
              type="number"
              value={formData.price}
              onChange={handleChange}
              margin="normal"
              required
              InputProps={{
                inputProps: { min: 0, step: "0.01" }
              }}
            />
            <TextField
              fullWidth
              label="Остаток"
              name="stock"
              type="number"
              value={formData.stock}
              onChange={handleChange}
              margin="normal"
              required
              InputProps={{
                inputProps: { min: 0, step: "1" }
              }}
            />
            {productSchema.map((field) => (
              field.type === 'select' ? (
                <FormControl fullWidth margin="normal" key={field.name}>
                  <InputLabel id={`label-${field.name}`}>{field.name}</InputLabel>
                  <Select
                    labelId={`label-${field.name}`}
                    name={field.name}
                    value={formData[field.name] ?? ''}
                    label={field.name}
                    onChange={handleChange}
                    MenuProps={{
                      PaperProps: { style: { width: '100%' } }
                    }}
                  >
                    {(field.options || []).map((opt) => (
                      <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : field.type === 'file' ? (
                <Box key={field.name} sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>{field.name}</Typography>
                  {renderFileField(field, false)}
                </Box>
              ) : field.type === 'image' ? (
                <Box key={field.name} sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>{field.name}</Typography>
                  {renderFileField(field, true)}
                </Box>
              ) : (
                <TextField
                  key={field.name}
                  fullWidth
                  label={field.name}
                  name={field.name}
                  value={formData[field.name] ?? ''}
                  onChange={handleChange}
                  margin="normal"
                />
              )
            ))}
            <TextField
              fullWidth
              label="Описание"
              name="description"
              value={formData.description}
              onChange={handleChange}
              margin="normal"
              multiline
              rows={3}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Отмена</Button>
            <Button type="submit" variant="contained">Сохранить</Button>
          </DialogActions>
        </form>
      </Dialog>

      <StoragePickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={(key) => { if (pickerTargetField && key) setFormData(prev => ({ ...prev, [pickerTargetField]: key })); setPickerOpen(false); }}
        imagesOnly={pickerImagesOnly}
        initialPrefix={`products/`}
      />

      <Button variant="outlined" onClick={openSchemaDialog} sx={{ ml: 2 }}>
        Изменить схему
      </Button>

      <Dialog open={schemaDialogOpen} onClose={closeSchemaDialog} maxWidth="md" fullWidth>
        <DialogTitle>Редактирование схемы товаров</DialogTitle>
        <DialogContent>
          <SchemaEditor schema={productSchema} onChange={setProductSchema} />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeSchemaDialog}>Отмена</Button>
          <Button variant="contained" onClick={handleSaveSchema}>Сохранить</Button>
        </DialogActions>
      </Dialog>

      <ExcelImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        endpoint="/products/import"
        onSuccess={fetchProducts}
      />
    </Layout>
  );
};

export default ProductsPage; 